import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import Timeline, { TimelineSegment } from '../Timeline';
import { buildSessionTimelineSegments } from '../../utils/buildSessionTimelineSegments';
import styles from './SessionList.module.css';

export interface SessionListActivity {
  id: number;
  typeOfWorkId?: number;
  typeOfWorkName?: string;
  timeStart?: string;
  timeEnd?: string;
  hours?: number;
  allocatedHours?: number;
  sortOrder?: number;
  amount?: number;
  /** Выплачено (зарплата) */
  paidAmount?: number;
  /** Выплачено премией */
  bonusPaidAmount?: number;
  /** Начисленная, но не оплаченная премия */
  unpaidBonusAmount?: number;
  remainingAmount?: number;
  rate?: number;
  hasFinancialAccess: boolean;
  comment?: string;
  isAdditionalWork?: boolean;
}

export interface Session {
  id: number;
  userId: number;
  userName?: string;
  /** Название объекта (для режима «общие смены») */
  siteName?: string;
  timeStart: string;
  timeEnd?: string;
  hours?: number;
  totalAmount?: number;
  paidAmount?: number;
  /** Начислено компенсаций (net, с учётом корректировок) */
  compensationAmount?: number;
  /** Выплачено компенсаций */
  compensationPaidAmount?: number;
  remainingAmount?: number;
  hasFinancialAccess: boolean;
  activities?: SessionListActivity[];
}

export interface SessionListProps {
  sessions: Session[];
  onSessionClick?: (session: Session) => void;
  onActionsClick?: (session: Session) => void;
  onActivityPaymentsClick?: (activity: SessionListActivity, sessionId: number) => void;
  onSessionSelect?: (session: Session) => void;
  selectedSessionIds?: Set<number>;
  /** Если передан — зона финансов цензурируется (размытие + глазик). Видимость по сессии. */
  isFinancialVisible?: (sessionId: number) => boolean;
  /** Переключить видимость финансов одной смены (по клику на глазик). */
  onToggleFinancialVisible?: (sessionId: number) => void;
  /** При клике на пометку «доп» — показать причину. siteId нужен для загрузки истории. */
  siteId?: number;
  onAdditionalWorkReasonClick?: (activity: SessionListActivity, sessionId: number) => void;
  /** При клике на комментарий — показать окно с текстом и фото комментария. */
  onCommentClick?: (activity: SessionListActivity, sessionId: number) => void;
  /** При клике на значок «!» (не указан вид работ) — например открыть редактирование смены. */
  onUnsetWorkTypeClick?: (sessionId: number) => void;
  formatTime?: (timeString: string) => string;
  formatDate?: (timeString: string) => string;
  formatDuration?: (hours?: number) => { hours: string; minutes: string | null };
  formatAmount?: (amount?: number) => string;
  loading?: boolean;
  loadingActivities?: Record<number, boolean>;
  emptyState?: {
    icon: string;
    title: string;
    subtitle?: string;
  };
}

// Мемоизированный компонент активности
const ActivityItem = React.memo<{
  activity: SessionListActivity;
  sessionId?: number;
  isFinancialVisible?: (sessionId: number) => boolean;
  onPaymentsClick?: (activity: SessionListActivity, sessionId: number) => void;
  onAdditionalWorkReasonClick?: (activity: SessionListActivity, sessionId: number) => void;
  onCommentClick?: (activity: SessionListActivity, sessionId: number) => void;
  formatTime: (timeString: string) => string;
  formatDuration: (hours?: number) => { hours: string; minutes: string | null };
  formatAmount: (amount?: number) => string;
}>(({ activity, sessionId, isFinancialVisible, onPaymentsClick, onAdditionalWorkReasonClick, onCommentClick, formatTime, formatDuration, formatAmount }) => {
  const isAbsence = activity.typeOfWorkName?.toLowerCase().includes('отсутствовал') || 
                   activity.typeOfWorkName?.toLowerCase().includes('отсутствие');
  
  const activityHours = activity.hours ?? activity.allocatedHours;
  const duration = formatDuration(activityHours);
  const durationDisplay = duration.minutes != null
    ? `${duration.hours}:${duration.minutes.padStart(2, '0')}`
    : `${duration.hours}:00`;

  // Определяем статус оплаты для активности
  let activityPaymentStatusClass = '';
  if (activity.hasFinancialAccess && activity.amount && activity.amount > 0) {
    const remainingAmount = activity.remainingAmount ?? 0;
    const paidAmount = activity.paidAmount ?? 0;
    const totalAmount = activity.amount;
    
    // Нормализуем значения для точного сравнения (округляем до 2 знаков)
    const normalizedRemaining = Math.round(remainingAmount * 100) / 100;
    const normalizedPaid = Math.round(paidAmount * 100) / 100;
    const normalizedTotal = Math.round(totalAmount * 100) / 100;
    
    // Считаем полностью оплаченной, если:
    // 1. Остаток <= 0.01 (учитываем возможные ошибки округления)
    // 2. Или выплачено >= наработано (с учетом округления)
    const isFullyPaid = normalizedRemaining <= 0.01 || 
                       normalizedPaid >= normalizedTotal || 
                       (normalizedPaid > 0 && Math.abs(normalizedPaid - normalizedTotal) < 0.01);
    const isPartiallyPaid = normalizedPaid > 0 && !isFullyPaid;
    
    activityPaymentStatusClass = isFullyPaid ? styles.paid : isPartiallyPaid ? styles.partial : styles.unpaid;
  }

  const activityRightContent = useMemo(() => {
    if (activity.hasFinancialAccess && activity.amount !== undefined && activity.amount !== null) {
      const premiumPaid = activity.bonusPaidAmount ?? 0;
      const premiumRemaining = activity.unpaidBonusAmount ?? 0;
      const premiumAccrued = premiumPaid + premiumRemaining;
      return (
        <div className={`${styles.financialContainer} ${activityPaymentStatusClass}`}>
          <div className={styles.paymentSection}>
            {!isAbsence && (
              <div className={styles.paymentRow}>
                <span className={styles.paymentLabel}>Наработано:</span>
                <span className={styles.paymentValue}>{formatAmount(activity.amount)}</span>
              </div>
            )}
            {activity.paidAmount !== undefined && activity.paidAmount !== null && activity.paidAmount > 0 && (
              <div className={styles.paymentRow}>
                <span className={styles.paymentLabel}>Выплачено (з/п):</span>
                <span className={styles.paymentValue}>{formatAmount(activity.paidAmount)}</span>
              </div>
            )}
            {premiumAccrued > 0.01 && (
              <div className={styles.paymentRow}>
                <span className={styles.paymentLabel}>Премия (начислено):</span>
                <span className={styles.paymentValue}>{formatAmount(premiumAccrued)}</span>
              </div>
            )}
            {premiumPaid > 0.01 && (
              <div className={styles.paymentRow}>
                <span className={styles.paymentLabel}>Выплачено (премия):</span>
                <span className={styles.paymentValue}>{formatAmount(premiumPaid)}</span>
              </div>
            )}
            {premiumRemaining > 0.01 && (
              <div className={styles.paymentRow}>
                <span className={styles.paymentLabel}>Остаток (премия):</span>
                <span className={`${styles.paymentValue} ${styles.remaining}`}>{formatAmount(premiumRemaining)}</span>
              </div>
            )}
            {(() => {
              const remaining = activity.remainingAmount ?? 0;
              const normalizedRemaining = Math.round(remaining * 100) / 100;
              return normalizedRemaining > 0.01 ? (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Остаток (з/п):</span>
                  <span className={`${styles.paymentValue} ${styles.remaining}`}>{formatAmount(remaining)}</span>
                </div>
              ) : null;
            })()}
          </div>
        </div>
      );
    } else if (activity.hasFinancialAccess === false) {
      return (
        <div className={`${styles.financialContainer} ${styles.noAccess}`}>
          <div className={styles.paymentRow}>
            <span className={`${styles.paymentLabel} ${styles.textMuted}`}>
              <i className="ri-lock-line"></i> Финансовая информация скрыта
            </span>
          </div>
        </div>
      );
    }
    return null;
  }, [activity, activityPaymentStatusClass, isAbsence, formatAmount]);

  // Маскированная версия финансового блока (звёздочки вместо цифр)
  const maskedFormatAmount = useCallback(() => {
    return '*** ₽';
  }, []);

  const maskedActivityRightContent = useMemo(() => {
    if (activity.hasFinancialAccess && activity.amount !== undefined && activity.amount !== null) {
      return (
        <div className={`${styles.financialContainer} ${activityPaymentStatusClass}`}>
          <div className={styles.paymentSection}>
            {!isAbsence && (
              <div className={styles.paymentRow}>
                <span className={styles.paymentLabel}>Наработано:</span>
                <span className={styles.paymentValue}>{maskedFormatAmount()}</span>
              </div>
            )}
            {activity.paidAmount !== undefined && activity.paidAmount !== null && activity.paidAmount > 0 && (
              <div className={styles.paymentRow}>
                <span className={styles.paymentLabel}>Выплачено:</span>
                <span className={styles.paymentValue}>{maskedFormatAmount()}</span>
              </div>
            )}
            {((activity.bonusPaidAmount ?? 0) + (activity.unpaidBonusAmount ?? 0)) > 0.01 && (
              <div className={styles.paymentRow}>
                <span className={styles.paymentLabel}>Премия (начислено):</span>
                <span className={styles.paymentValue}>{maskedFormatAmount()}</span>
              </div>
            )}
            {(activity.bonusPaidAmount ?? 0) > 0.01 && (
              <div className={styles.paymentRow}>
                <span className={styles.paymentLabel}>Выплачено (премия):</span>
                <span className={styles.paymentValue}>{maskedFormatAmount()}</span>
              </div>
            )}
            {(activity.unpaidBonusAmount ?? 0) > 0.01 && (
              <div className={styles.paymentRow}>
                <span className={styles.paymentLabel}>Остаток (премия):</span>
                <span className={`${styles.paymentValue} ${styles.remaining}`}>{maskedFormatAmount()}</span>
              </div>
            )}
            {(() => {
              const remaining = activity.remainingAmount ?? 0;
              const normalizedRemaining = Math.round(remaining * 100) / 100;
              return normalizedRemaining > 0.01 ? (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Остаток:</span>
                  <span className={`${styles.paymentValue} ${styles.remaining}`}>{maskedFormatAmount()}</span>
                </div>
              ) : null;
            })()}
          </div>
        </div>
      );
    } else if (activity.hasFinancialAccess === false) {
      return (
        <div className={`${styles.financialContainer} ${styles.noAccess}`}>
          <div className={styles.paymentRow}>
            <span className={`${styles.paymentLabel} ${styles.textMuted}`}>
              <i className="ri-lock-line"></i> Финансовая информация скрыта
            </span>
          </div>
        </div>
      );
    }
    return null;
  }, [activity, activityPaymentStatusClass, isAbsence, maskedFormatAmount]);

  return (
    <div 
      className={`${styles.activityItem} ${isAbsence ? styles.absenceActivity : ''} ${activityPaymentStatusClass || ''}`}
      onClick={() => sessionId !== undefined && onPaymentsClick && onPaymentsClick(activity, sessionId)}
      style={{
        cursor: onPaymentsClick ? 'pointer' : 'default'
      }}
    >
      <div className={`${styles.timeBlock} ${isAbsence ? styles.timeBlockAbsence : ''}`}>
        {activity.timeStart ? (
          <>
            <div className={styles.timeBlockStart}>
              {formatTime(activity.timeStart)}
            </div>
            <div className={styles.timeBlockDuration}>
              <div>{durationDisplay}</div>
            </div>
            {activity.timeEnd && (
              <div className={styles.timeBlockEnd}>
                {formatTime(activity.timeEnd)}
              </div>
            )}
          </>
        ) : (
          <div className={styles.timeBlockDuration}>
            <div>{durationDisplay}</div>
          </div>
        )}
      </div>
      <div className={styles.activityContent}>
        <div className={styles.activityTitle}>
          {activity.typeOfWorkName || 'Неизвестный вид работ'}
        </div>
        {activity.rate && activity.hasFinancialAccess && (
          <div className={styles.activityDetails}>
            <i className="ri-money-dollar-circle-line"></i>
            <span>{formatAmount(activity.rate)}/ч</span>
          </div>
        )}
        {activity.comment && (
          <button
            type="button"
            className={styles.activityCommentBadge}
            title={onCommentClick ? 'Нажмите, чтобы открыть комментарий с фото' : activity.comment}
            onClick={(e) => {
              e.stopPropagation();
              if (sessionId !== undefined && onCommentClick) {
                onCommentClick(activity, sessionId);
              }
            }}
            disabled={!onCommentClick}
          >
            <i className="ri-chat-3-line"></i>
            <span className={styles.activityCommentBadgeText}>{activity.comment}</span>
            {onCommentClick && <i className={`ri-arrow-right-s-line ${styles.activityBadgeArrow}`} aria-hidden />}
          </button>
        )}
        {activity.isAdditionalWork && (
          <button
            type="button"
            className={styles.additionalWorkBadge}
            onClick={(e) => {
              e.stopPropagation();
              if (sessionId !== undefined && onAdditionalWorkReasonClick) {
                onAdditionalWorkReasonClick(activity, sessionId);
              }
            }}
            title={onAdditionalWorkReasonClick ? 'Нажмите, чтобы узнать причину' : 'Доп.работа'}
            disabled={!onAdditionalWorkReasonClick}
          >
            <i className="ri-information-line"></i>
            доп
            {onAdditionalWorkReasonClick && <i className={`ri-arrow-right-s-line ${styles.activityBadgeArrow}`} aria-hidden />}
          </button>
        )}
      </div>
      {activityRightContent && (
        <div className={styles.activityRight}>
          {sessionId !== undefined && isFinancialVisible && !isFinancialVisible(sessionId) ? (
            <div className={styles.financialBlurWrapper} title="Нажмите на глазик в заголовке смены, чтобы показать финансы">
              <div className={styles.financialContentMasked}>{maskedActivityRightContent}</div>
              <div className={styles.financialCensorOverlayBlurOnly} aria-hidden />
            </div>
          ) : (
            activityRightContent
          )}
        </div>
      )}
    </div>
  );
});

ActivityItem.displayName = 'ActivityItem';

// Мемоизированный компонент сессии
const SessionItem = React.memo<{
  session: Session;
  activities: SessionListActivity[];
  isLoading: boolean;
  onSessionClick?: (session: Session) => void;
  onActionsClick?: (session: Session) => void;
  onActivityPaymentsClick?: (activity: SessionListActivity, sessionId: number) => void;
  onAdditionalWorkReasonClick?: (activity: SessionListActivity, sessionId: number) => void;
  onCommentClick?: (activity: SessionListActivity, sessionId: number) => void;
  onUnsetWorkTypeClick?: (sessionId: number) => void;
  onSessionSelect?: (session: Session) => void;
  isSelected?: boolean;
  isFinancialVisible?: (sessionId: number) => boolean;
  onToggleFinancialVisible?: (sessionId: number) => void;
  formatTime: (timeString: string) => string;
  formatDuration: (hours?: number) => { hours: string; minutes: string | null };
  formatAmount: (amount?: number) => string;
}>(({ session, activities = [], isLoading, onSessionClick, onActionsClick, onActivityPaymentsClick, onAdditionalWorkReasonClick, onCommentClick, onUnsetWorkTypeClick, onSessionSelect, isSelected = false, isFinancialVisible, onToggleFinancialVisible, formatTime, formatDuration, formatAmount }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [shouldSkipTransition, setShouldSkipTransition] = useState(false);
  const [wasSwiped, setWasSwiped] = useState(false);
  const swipeStartX = useRef(0);
  const swipeStartY = useRef(0);
  const headerRef = useRef<HTMLDivElement>(null);

  const toggleExpanded = useCallback((e?: React.MouseEvent) => {
    // Предотвращаем раскрытие если был свайп
    if (wasSwiped) {
      e?.preventDefault();
      e?.stopPropagation();
      setWasSwiped(false);
      return;
    }
    setIsExpanded(prev => !prev);
  }, [wasSwiped]);

  const handleSwipeStart = useCallback((clientX: number, clientY: number) => {
    swipeStartX.current = clientX;
    swipeStartY.current = clientY;
    setIsSwiping(true);
  }, []);

  const handleSwipeMove = useCallback((clientX: number, clientY: number) => {
    if (!isSwiping) return;

    const deltaX = clientX - swipeStartX.current;
    const deltaY = Math.abs(clientY - swipeStartY.current);

    // Игнорируем вертикальные свайпы
    if (deltaY > 10 && Math.abs(deltaX) < 10) {
      setIsSwiping(false);
      return;
    }

    // Свайп влево (отрицательное значение)
    if (deltaX < 0) {
      const offset = Math.max(deltaX, -80); // Максимум 80px влево
      setSwipeOffset(offset);
    } else if (swipeOffset < 0) {
      // Свайп вправо для возврата
      const offset = Math.min(deltaX + swipeOffset, 0);
      setSwipeOffset(offset);
    }
  }, [isSwiping, swipeOffset]);

  const handleSwipeEnd = useCallback(() => {
    setIsSwiping(false);

    // Если свайпнули достаточно далеко, выделяем/снимаем выделение
    if (swipeOffset < -60 && onSessionSelect) {
      // Помечаем что был свайп, чтобы предотвратить клик
      setWasSwiped(true);
      
      // Мгновенно возвращаем в исходное положение через прямой доступ к DOM
      // Делаем это синхронно, до любых обновлений React state
      if (headerRef.current) {
        // Убираем transition и сразу возвращаем в исходное положение
        headerRef.current.style.transition = 'none';
        headerRef.current.style.transform = 'translateX(0px)';
        // Принудительный reflow для немедленного применения изменений
        void headerRef.current.offsetHeight; // force reflow
      }
      
      // Обновляем React state после обновления DOM
      setShouldSkipTransition(true);
      setSwipeOffset(0);
      
      // Вызываем выделение асинхронно, чтобы не блокировать визуальное обновление
      setTimeout(() => {
        onSessionSelect(session);
        // Восстанавливаем transition после небольшой задержки
        setTimeout(() => {
          if (headerRef.current) {
            headerRef.current.style.transition = '';
          }
          setShouldSkipTransition(false);
          // Сбрасываем флаг свайпа после небольшой задержки
          setTimeout(() => {
            setWasSwiped(false);
          }, 100);
        }, 10);
      }, 0);
    } else {
      // Возвращаем в исходное положение с анимацией
      setSwipeOffset(0);
      setWasSwiped(false);
    }
  }, [swipeOffset, onSessionSelect, session]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    // Не запускаем свайп если кликнули по кнопке или ссылке
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) {
      return;
    }
    handleSwipeStart(e.touches[0].clientX, e.touches[0].clientY);
  }, [handleSwipeStart]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (isSwiping) {
      handleSwipeMove(e.touches[0].clientX, e.touches[0].clientY);
    }
  }, [isSwiping, handleSwipeMove]);

  const handleTouchEnd = useCallback(() => {
    if (isSwiping) {
      handleSwipeEnd();
    }
  }, [isSwiping, handleSwipeEnd]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    // Не запускаем свайп если кликнули по кнопке или ссылке
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a')) {
      return;
    }
    handleSwipeStart(e.clientX, e.clientY);
  }, [handleSwipeStart]);

  useEffect(() => {
    if (!isSwiping) return;

    const handleMouseMove = (e: MouseEvent) => {
      handleSwipeMove(e.clientX, e.clientY);
    };

    const handleMouseUp = () => {
      handleSwipeEnd();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isSwiping, handleSwipeMove, handleSwipeEnd]);

  const sessionHours = session.hours ?? activities.reduce(
    (sum, a) => sum + (a.hours ?? a.allocatedHours ?? 0),
    0
  );
  const duration = formatDuration(sessionHours);
  const durationDisplay = duration.minutes != null
    ? `${duration.hours}:${duration.minutes.padStart(2, '0')}`
    : `${duration.hours}:00`;

  // Вычисляем сегменты для временной шкалы
  const timelineSegments = useMemo(
    (): TimelineSegment[] =>
      buildSessionTimelineSegments(activities, session.timeStart, session.timeEnd),
    [activities, session.timeStart, session.timeEnd]
  );

  // Вычисляем точки перетаскивания (handles) между активностями (только для time-based)
  const timelineHandles = useMemo(() => {
    if (activities.length < 2) return [];
    const sortedActivities = [...activities].sort((a, b) => {
      const oA = a.sortOrder ?? 999999;
      const oB = b.sortOrder ?? 999999;
      if (oA !== oB) return oA - oB;
      const tA = a.timeStart ? new Date(a.timeStart).getTime() : 0;
      const tB = b.timeStart ? new Date(b.timeStart).getTime() : 0;
      return tA - tB;
    });
    return sortedActivities.slice(1)
      .filter(a => a.timeStart)
      .map((activity, index) => ({
        id: `handle-${index}`,
        time: new Date(activity.timeStart!)
      }));
  }, [activities]);

  // Есть ли среди активностей смены комментарии или доп.работа (для ярлычков в заголовке)
  const hasActivitiesWithComment = useMemo(
    () => activities.some((a) => (a.comment ?? '').trim().length > 0),
    [activities]
  );
  const hasActivitiesWithAdditionalWork = useMemo(
    () => activities.some((a) => a.isAdditionalWork === true),
    [activities]
  );
  // Есть ли среди активностей смены активности с не указанным видом работ
  const hasActivitiesWithUnsetWorkType = useMemo(
    () => activities.some((a) => !a.typeOfWorkId || a.typeOfWorkId <= 1),
    [activities]
  );

  // Пересчитываем финансовую информацию сессии на основе активностей
  const sessionActivitiesFinancial = useMemo(() => {
    return activities.reduce(
      (acc, activity) => {
        if (activity.hasFinancialAccess) {
          if (activity.amount) acc.totalAmount += activity.amount;
          if (activity.paidAmount) acc.paidAmount += activity.paidAmount;
          if (activity.bonusPaidAmount) acc.bonusPaidAmount += activity.bonusPaidAmount;
          if (activity.remainingAmount) acc.remainingAmount += activity.remainingAmount;
        }
        return acc;
      },
      { totalAmount: 0, paidAmount: 0, bonusPaidAmount: 0, remainingAmount: 0 }
    );
  }, [activities]);

  // Определяем статус оплаты
  let paymentStatusClass = '';
  if (session.hasFinancialAccess) {
    if (activities.length > 0) {
      const remainingAmount = sessionActivitiesFinancial.remainingAmount ?? 0;
      const paidAmount = sessionActivitiesFinancial.paidAmount ?? 0;
      const totalAmount = sessionActivitiesFinancial.totalAmount ?? 0;
      
      // Нормализуем значения для точного сравнения (округляем до 2 знаков)
      const normalizedRemaining = Math.round(remainingAmount * 100) / 100;
      const normalizedPaid = Math.round(paidAmount * 100) / 100;
      const normalizedTotal = Math.round(totalAmount * 100) / 100;
      
      // Считаем полностью оплаченной, если:
      // 1. Остаток <= 0.01 (учитываем возможные ошибки округления)
      // 2. Или выплачено >= наработано (с учетом округления)
      const isFullyPaid = normalizedRemaining <= 0.01 || 
                         normalizedPaid >= normalizedTotal || 
                         (normalizedPaid > 0 && normalizedTotal > 0 && Math.abs(normalizedPaid - normalizedTotal) < 0.01);
      const isPartiallyPaid = normalizedPaid > 0 && !isFullyPaid;
      paymentStatusClass = isFullyPaid ? styles.paid : isPartiallyPaid ? styles.partial : styles.unpaid;
    } else if (!isLoading) {
      const remainingAmount = session.remainingAmount ?? 0;
      const paidAmount = session.paidAmount ?? 0;
      const totalAmount = session.totalAmount ?? 0;
      
      // Нормализуем значения для точного сравнения (округляем до 2 знаков)
      const normalizedRemaining = Math.round(remainingAmount * 100) / 100;
      const normalizedPaid = Math.round(paidAmount * 100) / 100;
      const normalizedTotal = Math.round(totalAmount * 100) / 100;
      
      // Считаем полностью оплаченной, если:
      // 1. Остаток <= 0.01 (учитываем возможные ошибки округления)
      // 2. Или выплачено >= наработано (с учетом округления)
      const isFullyPaid = normalizedRemaining <= 0.01 || 
                         normalizedPaid >= normalizedTotal || 
                         (normalizedPaid > 0 && normalizedTotal > 0 && Math.abs(normalizedPaid - normalizedTotal) < 0.01);
      const isPartiallyPaid = normalizedPaid > 0 && !isFullyPaid;
      paymentStatusClass = isFullyPaid ? styles.paid : isPartiallyPaid ? styles.partial : styles.unpaid;
    }
  }

  const sessionRightContent = useMemo(() => {
    if (session.hasFinancialAccess) {
      if (activities.length > 0) {
        return (
          <div className={`${styles.financialContainer} ${paymentStatusClass}`}>
            <div className={styles.paymentSection}>
              {sessionActivitiesFinancial.totalAmount !== undefined && sessionActivitiesFinancial.totalAmount !== null && (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Наработано:</span>
                  <span className={styles.paymentValue}>{formatAmount(sessionActivitiesFinancial.totalAmount)}</span>
                </div>
              )}
              {sessionActivitiesFinancial.paidAmount !== undefined &&
                sessionActivitiesFinancial.paidAmount !== null &&
                sessionActivitiesFinancial.paidAmount > 0 && (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Выплачено (з/п):</span>
                  <span className={styles.paymentValue}>{formatAmount(sessionActivitiesFinancial.paidAmount)}</span>
                </div>
              )}
              {sessionActivitiesFinancial.bonusPaidAmount !== undefined &&
                sessionActivitiesFinancial.bonusPaidAmount !== null &&
                (sessionActivitiesFinancial.bonusPaidAmount > 0 || activities.some(a => (a.unpaidBonusAmount ?? 0) > 0.01)) && (
                  <div className={styles.paymentRow}>
                    <span className={styles.paymentLabel}>Премия (начислено):</span>
                    <span className={styles.paymentValue}>
                      {formatAmount((sessionActivitiesFinancial.bonusPaidAmount ?? 0) + activities.reduce((acc, a) => acc + (a.unpaidBonusAmount ?? 0), 0))}
                    </span>
                  </div>
                )}
              {sessionActivitiesFinancial.bonusPaidAmount !== undefined &&
                sessionActivitiesFinancial.bonusPaidAmount !== null &&
                sessionActivitiesFinancial.bonusPaidAmount > 0 && (
                  <div className={styles.paymentRow}>
                    <span className={styles.paymentLabel}>Выплачено (премия):</span>
                    <span className={styles.paymentValue}>{formatAmount(sessionActivitiesFinancial.bonusPaidAmount)}</span>
                  </div>
                )}
              {(() => {
                const unpaidBonus = activities.reduce((acc, a) => acc + (a.unpaidBonusAmount ?? 0), 0);
                return unpaidBonus > 0.01 ? (
                  <div className={styles.paymentRow}>
                    <span className={styles.paymentLabel}>Остаток (премия):</span>
                    <span className={`${styles.paymentValue} ${styles.remaining}`}>{formatAmount(unpaidBonus)}</span>
                  </div>
                ) : null;
              })()}
              {session.compensationAmount !== undefined && session.compensationAmount !== null && Math.abs(session.compensationAmount) > 0.01 && (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Компенсация:</span>
                  <span className={styles.paymentValue}>{formatAmount(session.compensationAmount)}</span>
                </div>
              )}
              {session.compensationPaidAmount !== undefined && session.compensationPaidAmount !== null && session.compensationPaidAmount > 0.01 && (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Выплачено (компенс.):</span>
                  <span className={styles.paymentValue}>{formatAmount(session.compensationPaidAmount)}</span>
                </div>
              )}
              {(() => {
                const remaining = sessionActivitiesFinancial.remainingAmount ?? 0;
                const normalizedRemaining = Math.round(remaining * 100) / 100;
                return normalizedRemaining > 0.01 ? (
                  <div className={styles.paymentRow}>
                    <span className={styles.paymentLabel}>Остаток (з/п):</span>
                    <span className={`${styles.paymentValue} ${styles.remaining}`}>{formatAmount(remaining)}</span>
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        );
      } else if (!isLoading) {
        return (
          <div className={`${styles.financialContainer} ${paymentStatusClass}`}>
            <div className={styles.paymentSection}>
              {session.totalAmount !== undefined && session.totalAmount !== null && (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Наработано:</span>
                  <span className={styles.paymentValue}>{formatAmount(session.totalAmount)}</span>
                </div>
              )}
              {session.paidAmount !== undefined && session.paidAmount !== null && session.paidAmount > 0 && (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Выплачено:</span>
                  <span className={styles.paymentValue}>{formatAmount(session.paidAmount)}</span>
                </div>
              )}
              {session.compensationAmount !== undefined && session.compensationAmount !== null && Math.abs(session.compensationAmount) > 0.01 && (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Компенсация:</span>
                  <span className={styles.paymentValue}>{formatAmount(session.compensationAmount)}</span>
                </div>
              )}
              {session.compensationPaidAmount !== undefined && session.compensationPaidAmount !== null && session.compensationPaidAmount > 0.01 && (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Выплачено (компенс.):</span>
                  <span className={styles.paymentValue}>{formatAmount(session.compensationPaidAmount)}</span>
                </div>
              )}
              {(() => {
                const remaining = session.remainingAmount ?? 0;
                const normalizedRemaining = Math.round(remaining * 100) / 100;
                return normalizedRemaining > 0.01 ? (
                  <div className={styles.paymentRow}>
                    <span className={styles.paymentLabel}>Остаток:</span>
                    <span className={`${styles.paymentValue} ${styles.remaining}`}>{formatAmount(remaining)}</span>
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        );
      }
    } else {
      return (
        <div className={`${styles.financialContainer} ${styles.noAccess}`}>
          <div className={styles.paymentSection}>
            <div className={styles.paymentRow}>
              <span className={`${styles.paymentLabel} ${styles.textMuted}`}>
                <i className="ri-lock-line"></i> Финансовая информация скрыта
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }, [session, activities, isLoading, sessionActivitiesFinancial, paymentStatusClass, formatAmount]);

  // Маскированная версия финансового блока для сессии (звёздочки)
  const maskedSessionRightContent = useMemo(() => {
    if (session.hasFinancialAccess) {
      if (activities.length > 0) {
        return (
          <div className={`${styles.financialContainer} ${paymentStatusClass}`}>
            <div className={styles.paymentSection}>
              {sessionActivitiesFinancial.totalAmount !== undefined && sessionActivitiesFinancial.totalAmount !== null && (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Наработано:</span>
                  <span className={styles.paymentValue}>{'*** ₽'}</span>
                </div>
              )}
              {sessionActivitiesFinancial.paidAmount !== undefined && sessionActivitiesFinancial.paidAmount !== null && sessionActivitiesFinancial.paidAmount > 0 && (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Выплачено:</span>
                  <span className={styles.paymentValue}>{'*** ₽'}</span>
                </div>
              )}
              {session.compensationAmount !== undefined && session.compensationAmount !== null && Math.abs(session.compensationAmount) > 0.01 && (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Компенсация:</span>
                  <span className={styles.paymentValue}>{'*** ₽'}</span>
                </div>
              )}
              {(() => {
                const remaining = sessionActivitiesFinancial.remainingAmount ?? 0;
                const normalizedRemaining = Math.round(remaining * 100) / 100;
                return normalizedRemaining > 0.01 ? (
                  <div className={styles.paymentRow}>
                    <span className={styles.paymentLabel}>Остаток:</span>
                    <span className={`${styles.paymentValue} ${styles.remaining}`}>{'*** ₽'}</span>
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        );
      } else if (!isLoading) {
        return (
          <div className={`${styles.financialContainer} ${paymentStatusClass}`}>
            <div className={styles.paymentSection}>
              {session.totalAmount !== undefined && session.totalAmount !== null && (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Наработано:</span>
                  <span className={styles.paymentValue}>{'*** ₽'}</span>
                </div>
              )}
              {session.paidAmount !== undefined && session.paidAmount !== null && session.paidAmount > 0 && (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Выплачено:</span>
                  <span className={styles.paymentValue}>{'*** ₽'}</span>
                </div>
              )}
              {session.compensationAmount !== undefined && session.compensationAmount !== null && Math.abs(session.compensationAmount) > 0.01 && (
                <div className={styles.paymentRow}>
                  <span className={styles.paymentLabel}>Компенсация:</span>
                  <span className={styles.paymentValue}>{'*** ₽'}</span>
                </div>
              )}
              {(() => {
                const remaining = session.remainingAmount ?? 0;
                const normalizedRemaining = Math.round(remaining * 100) / 100;
                return normalizedRemaining > 0.01 ? (
                  <div className={styles.paymentRow}>
                    <span className={styles.paymentLabel}>Остаток:</span>
                    <span className={`${styles.paymentValue} ${styles.remaining}`}>{'*** ₽'}</span>
                  </div>
                ) : null;
              })()}
            </div>
          </div>
        );
      }
    } else {
      return (
        <div className={`${styles.financialContainer} ${styles.noAccess}`}>
          <div className={styles.paymentSection}>
            <div className={styles.paymentRow}>
              <span className={`${styles.paymentLabel} ${styles.textMuted}`}>
                <i className="ri-lock-line"></i> Финансовая информация скрыта
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }, [session, activities, isLoading, sessionActivitiesFinancial, paymentStatusClass]);

  return (
    <div className={`${styles.sessionItem} ${paymentStatusClass} ${isSelected ? styles.selected : ''}`} data-expanded={isExpanded}>
      <div className={styles.sessionWrapper}>
        {/* Иконка выделения под карточкой */}
        {onSessionSelect && (
          <div 
            className={styles.swipeAction}
            style={{
              opacity: Math.min(Math.abs(swipeOffset) / 60, 1)
            }}
          >
            <i className={isSelected ? "ri-checkbox-circle-fill" : "ri-checkbox-circle-line"}></i>
          </div>
        )}
        
        {/* Сама карточка с свайпом */}
        <div 
          ref={headerRef}
          className={`${styles.sessionHeader} ${isSelected ? styles.sessionHeaderSelected : ''} ${shouldSkipTransition ? styles.noTransition : ''}`}
          data-no-tab-swipe
          style={{
            transform: `translateX(${swipeOffset}px)`,
            transition: (isSwiping || shouldSkipTransition) ? 'none' : undefined
          }}
          onClick={(e) => toggleExpanded(e)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}
          title={isExpanded ? 'Нажмите, чтобы свернуть' : 'Нажмите, чтобы раскрыть'}
        >
          <div 
            className={styles.sessionLeft}
          >
            <div className={styles.timeBlock}>
              <div className={styles.timeBlockStart}>
                {formatTime(session.timeStart)}
              </div>
              <div className={styles.timeBlockDuration}>
                <div>{durationDisplay}</div>
              </div>
              {session.timeEnd && (
                <div className={styles.timeBlockEnd}>
                  {formatTime(session.timeEnd)}
                </div>
              )}
            </div>
            <div className={styles.sessionInfo}>
              {session.siteName && (
                <div className={styles.sessionSiteName}>{session.siteName}</div>
              )}
              <div className={styles.sessionUserName}>
                {session.userName || `Пользователь ${session.userId}`}
              </div>
              {(hasActivitiesWithComment || hasActivitiesWithAdditionalWork || hasActivitiesWithUnsetWorkType) && (
                <div className={styles.sessionHeaderBadges}>
                  {hasActivitiesWithUnsetWorkType && (
                    <span
                      className={`${styles.sessionUnsetWorkTypeBadge} ${onUnsetWorkTypeClick ? styles.sessionUnsetWorkTypeBadgeClickable : ''}`}
                      title="В смене есть активности с не указанным видом работ. Нажмите, чтобы подробнее."
                      onClick={onUnsetWorkTypeClick ? (e) => { e.stopPropagation(); onUnsetWorkTypeClick(session.id); } : undefined}
                      role={onUnsetWorkTypeClick ? 'button' : undefined}
                      tabIndex={onUnsetWorkTypeClick ? 0 : undefined}
                      onKeyDown={onUnsetWorkTypeClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onUnsetWorkTypeClick(session.id); } } : undefined}
                    >
                      <i className="ri-error-warning-line"></i>
                    </span>
                  )}
                  {hasActivitiesWithComment && (
                    <span className={styles.sessionCommentBadge} title="В активностях смены есть комментарии">
                      <i className="ri-chat-3-line"></i>
                      комент
                    </span>
                  )}
                  {hasActivitiesWithAdditionalWork && (
                    <span className={styles.sessionAdditionalWorkBadge} title="В активностях смены есть доп.работа">
                      <i className="ri-information-line"></i>
                      доп
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          {sessionRightContent && (
            <div className={styles.sessionRight}>
              {session.hasFinancialAccess && isFinancialVisible && onToggleFinancialVisible ? (
                !isFinancialVisible(session.id) ? (
                  <div className={styles.financialBlurWrapper}>
                    <div className={styles.financialContentMasked}>{maskedSessionRightContent}</div>
                    <div
                      className={styles.financialCensorOverlayBlur}
                      onClick={(e) => { e.stopPropagation(); onToggleFinancialVisible(session.id); }}
                      title="Показать финансовую информацию"
                    >
                      <button type="button" className={styles.financialCensorEyeContour} aria-label="Показать финансы">
                        <i className="ri-eye-line"></i>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div
                    className={styles.financialClickToHide}
                    onClick={(e) => { e.stopPropagation(); onToggleFinancialVisible(session.id); }}
                    title="Нажмите, чтобы скрыть финансы"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleFinancialVisible(session.id); } }}
                    aria-label="Скрыть финансы"
                  >
                    {sessionRightContent}
                  </div>
                )
              ) : (
                sessionRightContent
              )}
            </div>
          )}
        </div>
      </div>
      
      <div className={styles.sessionActivities}>
        {isLoading && activities.length === 0 ? (
          <div className={styles.loadingState}>
            <i className="ri-loader-4-line ri-spin"></i>
            <span>Загрузка активностей...</span>
          </div>
        ) : activities.length > 0 ? (
          <>
            {/* Временная шкала - показываем даже для одной активности */}
            {activities.length >= 1 && (
              <div className={styles.timelineWrapper}>
                <Timeline
                  startTime={new Date(session.timeStart)}
                  endTime={session.timeEnd ? new Date(session.timeEnd) : new Date()}
                  segments={timelineSegments}
                  handles={timelineHandles}
                  formatTime={(date: Date) => formatTime(date.toISOString())}
                  formatDuration={(hours: number) => {
                    const h = Math.floor(hours);
                    const m = Math.round((hours - h) * 60);
                    if (m === 0) return `${h}ч`;
                    return `${h}ч ${m}м`;
                  }}
                  minSegmentWidth={8}
                  showTimeLabels={true}
                  showDuration={true}
                  showLabels={true}
                  draggable={false}
                  className={styles.sessionTimeline}
                />
              </div>
            )}
            {/* Список активностей */}
            {activities.map((activity) => (
              <ActivityItem
                key={activity.id}
                activity={activity}
                sessionId={session.id}
                isFinancialVisible={isFinancialVisible}
                onPaymentsClick={onActivityPaymentsClick}
                onAdditionalWorkReasonClick={onAdditionalWorkReasonClick}
                onCommentClick={onCommentClick}
                formatTime={formatTime}
                formatDuration={formatDuration}
                formatAmount={formatAmount}
              />
            ))}
          </>
        ) : (
          <div className={styles.emptyState}>
            <i className="ri-inbox-line"></i>
            <span>Активности не найдены</span>
          </div>
        )}
      </div>
    </div>
  );
});

SessionItem.displayName = 'SessionItem';

// Мемоизированный компонент заголовка даты
const DateHeader = React.memo<{
  date: string;
}>(({ date }) => {
  return (
    <div className={styles.dateHeader}>
      {date}
    </div>
  );
});

DateHeader.displayName = 'DateHeader';

// Основной компонент списка сессий
const SessionList: React.FC<SessionListProps> = ({
  sessions,
  onSessionClick,
  onActionsClick,
  onActivityPaymentsClick,
  onAdditionalWorkReasonClick,
  onCommentClick,
  onUnsetWorkTypeClick,
  onSessionSelect,
  selectedSessionIds = new Set(),
  isFinancialVisible,
  onToggleFinancialVisible,
  formatTime,
  formatDate,
  formatDuration,
  formatAmount,
  loading = false,
  loadingActivities = {},
  emptyState
}) => {
  // Функции форматирования по умолчанию
  const defaultFormatTime = useCallback((timeString: string) => {
    return new Date(timeString).toLocaleTimeString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }, []);

  const defaultFormatDate = useCallback((timeString: string) => {
    const date = new Date(timeString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Сегодня';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Вчера';
    } else {
      return date.toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
  }, []);

  const defaultFormatDuration = useCallback((hours?: number) => {
    if (!hours) return { hours: '0', minutes: null };
    
    const wholeHours = Math.floor(hours);
    let minutes = Math.round((hours - wholeHours) * 60);
    
    if (minutes >= 60) {
      const additionalHours = Math.floor(minutes / 60);
      minutes = minutes % 60;
      const totalHours = wholeHours + additionalHours;
      if (minutes === 0) {
        return { hours: `${totalHours}`, minutes: null };
      }
      return { hours: `${totalHours}`, minutes: `${minutes}` };
    }
    
    if (minutes === 0) {
      return { hours: `${wholeHours}`, minutes: null };
    }
    return { hours: `${wholeHours}`, minutes: `${minutes}` };
  }, []);

  const defaultFormatAmount = useCallback((amount?: number) => {
    if (amount === undefined || amount === null) return '—';
    return `${amount.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₽`;
  }, []);

  const finalFormatTime = formatTime || defaultFormatTime;
  const finalFormatDate = formatDate || defaultFormatDate;
  const finalFormatDuration = formatDuration || defaultFormatDuration;
  const finalFormatAmount = formatAmount || defaultFormatAmount;

  // Группируем сессии по дате и пользователю
  const groupedSessions = useMemo(() => {
    const groups: { [key: string]: { [userId: string]: { userName: string; sessions: Session[] } } } = {};

    sessions.forEach(session => {
      const date = finalFormatDate(session.timeStart);
      const userId = session.userId.toString();
      const userName = session.userName || `Пользователь ${session.userId}`;
      
      if (!groups[date]) {
        groups[date] = {};
      }
      if (!groups[date][userId]) {
        groups[date][userId] = {
          userName,
          sessions: []
        };
      }
      groups[date][userId].sessions.push(session);
    });

    return Object.entries(groups).sort((a, b) => {
      const dateA = new Date(a[1][Object.keys(a[1])[0]].sessions[0].timeStart);
      const dateB = new Date(b[1][Object.keys(b[1])[0]].sessions[0].timeStart);
      return dateB.getTime() - dateA.getTime();
    });
  }, [sessions, finalFormatDate]);

  if (loading) {
    return (
      <div className={styles.loadingState}>
        <i className="ri-loader-4-line ri-spin"></i>
        <span>Загрузка смен...</span>
      </div>
    );
  }

  if (sessions.length === 0) {
    if (emptyState) {
      return (
        <div className={styles.emptyState}>
          <i className={emptyState.icon}></i>
          <span>{emptyState.title}</span>
          {emptyState.subtitle && <small>{emptyState.subtitle}</small>}
        </div>
      );
    }
    return null;
  }

  return (
    <div className={styles.sessionList}>
      {groupedSessions.map(([date, userGroups]) => (
        <React.Fragment key={date}>
          <DateHeader date={date} />
          {Object.entries(userGroups).map(([userId, userData]) => (
              userData.sessions.map((session) => (
              <SessionItem
                key={session.id}
                session={session}
                activities={session.activities || []}
                isLoading={loadingActivities[session.id] || false}
                onSessionClick={onSessionClick}
                onActionsClick={onActionsClick}
                onActivityPaymentsClick={onActivityPaymentsClick}
                onAdditionalWorkReasonClick={onAdditionalWorkReasonClick}
                onCommentClick={onCommentClick}
                onUnsetWorkTypeClick={onUnsetWorkTypeClick}
                onSessionSelect={onSessionSelect}
                isSelected={selectedSessionIds.has(session.id)}
                isFinancialVisible={isFinancialVisible}
                onToggleFinancialVisible={onToggleFinancialVisible}
                formatTime={finalFormatTime}
                formatDuration={finalFormatDuration}
                formatAmount={finalFormatAmount}
              />
            ))
          ))}
        </React.Fragment>
      ))}
    </div>
  );
};

export default SessionList;

